"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Icon, { ICON_FLIP, ICON_SIZES } from "../library/Icon";
import {
  FEEDBACK_CATEGORIES,
  type FeedbackCategory,
  type FeedbackRating,
} from "./feedback";

type Props = {
  rating?: FeedbackRating;
  onFeedback: (
    rating: FeedbackRating | null,
    categories?: FeedbackCategory[],
    comment?: string,
  ) => void | Promise<void>;
};

export function GailMessageFeedback({ rating, onFeedback }: Props) {
  const { t } = useTranslation("ask");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<FeedbackCategory[]>([]);
  const [comment, setComment] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thankYou, setThankYou] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const thankYouTimer = useRef<number | null>(null);

  useEffect(() => {
    if (open && selected.length > 0) textareaRef.current?.focus();
  }, [open, selected.length]);

  useEffect(() => {
    return () => {
      if (thankYouTimer.current !== null) window.clearTimeout(thankYouTimer.current);
    };
  }, []);

  function closeDialog() {
    setOpen(false);
    setSelected([]);
    setComment("");
  }

  async function send(
    next: FeedbackRating | null,
    categories?: FeedbackCategory[],
    note?: string,
  ) {
    setPending(true);
    setError(null);
    try {
      if (categories || note) await onFeedback(next, categories, note);
      else await onFeedback(next);
      if (next) {
        setThankYou(true);
        if (thankYouTimer.current !== null) window.clearTimeout(thankYouTimer.current);
        thankYouTimer.current = window.setTimeout(() => setThankYou(false), 3000);
      }
    } catch {
      setError(t("feedback.saveFailed"));
    } finally {
      setPending(false);
    }
  }

  const placeholder = (() => {
    if (selected.length !== 1) return t("feedback.commentPlaceholder");
    const key = `feedback.commentPlaceholders.${selected[0]}`;
    const translated = t(key);
    return translated === key ? t("feedback.commentPlaceholder") : translated;
  })();

  return (
    <>
      <button
        type="button"
        className={`gpt-feedback-thumb${rating === "ThumbsUp" ? " is-on" : ""}`}
        aria-pressed={rating === "ThumbsUp"}
        aria-label={t("feedback.thumbsUp")}
        disabled={pending}
        hidden={rating === "ThumbsDown"}
        onClick={() => {
          void send(rating === "ThumbsUp" ? null : "ThumbsUp");
        }}
      >
        <Icon icon="thumbs-up" size={ICON_SIZES.SM} />
      </button>
      <button
        type="button"
        className={`gpt-feedback-thumb${rating === "ThumbsDown" ? " is-on" : ""}`}
        aria-pressed={rating === "ThumbsDown"}
        aria-label={t("feedback.thumbsDown")}
        disabled={pending}
        hidden={rating === "ThumbsUp"}
        onClick={() => {
          if (rating === "ThumbsDown") {
            void send(null);
            return;
          }
          setError(null);
          setOpen(true);
        }}
      >
        <Icon icon="thumbs-down" size={ICON_SIZES.SM} flip={ICON_FLIP.HORIZONTAL} />
      </button>
      {error ? <span className="gpt-feedback-error">{error}</span> : null}
      {thankYou
        ? createPortal(
            <div className="gpt-feedback-toast" role="status">
              {t("feedback.thankYou")}
            </div>,
            document.body,
          )
        : null}
      {open
        ? createPortal(
        <div className="gpt-feedback-backdrop" role="presentation" onMouseDown={closeDialog}>
          <div
            className="gpt-feedback-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gpt-feedback-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <h2 id="gpt-feedback-title">{t("feedback.dialogTitle")}</h2>
              <p>{t("feedback.dialogSubtitle")}</p>
            </header>
            <p className="gpt-feedback-label">{t("feedback.categoryLabel")}</p>
            <div className="gpt-feedback-categories">
              {FEEDBACK_CATEGORIES.map((category) => {
                const active = selected.includes(category);
                return (
                  <button
                    key={category}
                    type="button"
                    aria-pressed={active}
                    className={active ? "is-on" : ""}
                    onClick={() => {
                      setSelected((current) =>
                        current.includes(category)
                          ? current.filter((item) => item !== category)
                          : [...current, category],
                      );
                    }}
                  >
                    {active ? <Icon icon="check" size={ICON_SIZES.XS} /> : null}
                    {t(`feedback.categories.${category}`)}
                  </button>
                );
              })}
            </div>
            <textarea
              ref={textareaRef}
              rows={3}
              value={comment}
              placeholder={placeholder}
              onChange={(event) => setComment(event.target.value)}
            />
            <footer>
              <button type="button" className="gpt-feedback-cancel" onClick={closeDialog}>
                {t("feedback.cancel")}
              </button>
              <button
                type="button"
                className="gpt-feedback-submit"
                disabled={selected.length === 0 || pending}
                onClick={() => {
                  const categories = selected;
                  const note = comment.trim() || undefined;
                  closeDialog();
                  void send("ThumbsDown", categories, note);
                }}
              >
                {t("feedback.submit")}
              </button>
            </footer>
          </div>
        </div>,
        document.body,
      )
        : null}
    </>
  );
}
